import { useCallback, useMemo } from 'react';

import { isNil } from 'lodash-es';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { StoreProxy } from 'state/store/hooks';
import { isMobileOrderingAvailable, isRestaurantOpen, useGetRestaurantFn } from 'utils/restaurant';

export type ServiceModeStatus = {
  [key in ServiceMode]: {
    /**
     * store is available in MDM
     */
    capable: boolean;
    /**
     * store is available if store hours are within service mode availability
     * and the store supports this service mode type
     */
    available: boolean;
    /**
     * state is disabled based on flag from LD, separate from store's availability
     */
    disabled: boolean;
    /**
     * store hours are open within service mode availability
     */
    open?: boolean;
  };
};

export const useServiceModeStatus = (store: StoreProxy) => {
  const { serviceMode } = useServiceModeContext();
  const storeSelection2_0Enabled = useFlag(LaunchDarklyFlag.ENABLE_STORE_SELECTION_2_0);

  // Move these up in order to check for null (meaning LD service is down) or undefined
  // FYI - use ENABLE_* flags to avoid this necessity as null values will correctly disable the option
  const driveThruDisabled = useFlag(LaunchDarklyFlag.DISABLE_DRIVE_THRU);
  const dineInDisabled = useFlag(LaunchDarklyFlag.DISABLE_DINE_IN);
  const takeOutDisabled = useFlag(LaunchDarklyFlag.DISABLE_TAKE_OUT);

  const getRestaurant = useGetRestaurantFn();

  const serviceModeStatus: ServiceModeStatus = {
    CATERING_DELIVERY: {
      capable: !!store.hasDelivery && !!store.hasCatering,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CATERING_DELIVERY)
    },
    CATERING_PICKUP: {
      capable: !!store.hasCatering,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CATERING)
    },
    CURBSIDE: {
      capable: !!store.hasCurbside,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_CURBSIDE),
      open: false
    },
    DELIVERY: {
      capable: !!store.hasDelivery,
      available: false,
      disabled: !useFlag(LaunchDarklyFlag.ENABLE_DELIVERY),
      open: false
    },
    DRIVE_THRU: {
      capable: !!store.hasDriveThru,
      available: false,
      disabled: isNil(driveThruDisabled) || driveThruDisabled,
      open: false
    },
    EAT_IN: {
      capable: !!store.hasDineIn,
      available: false,
      disabled: isNil(dineInDisabled) || dineInDisabled,
      open: false
    },
    TAKEOUT: {
      capable: !!store.hasTakeOut,
      available: false,
      disabled: isNil(takeOutDisabled) || takeOutDisabled,
      open: false
    }
  };

  const curbsideOpen = isRestaurantOpen(store.curbsideHours);
  const deliveryOpen = isRestaurantOpen(store.deliveryHours);
  const diningRoomOpen = isRestaurantOpen(store.diningRoomHours);
  const driveThruOpen = isRestaurantOpen(store.driveThruHours);

  /** Need to know if service mode is open for store selection 2.0 */
  serviceModeStatus.DELIVERY.open = deliveryOpen;
  serviceModeStatus.EAT_IN.open = diningRoomOpen;
  serviceModeStatus.TAKEOUT.open = diningRoomOpen;
  serviceModeStatus.CURBSIDE.open = curbsideOpen;
  serviceModeStatus.DRIVE_THRU.open = driveThruOpen;

  serviceModeStatus.CATERING_DELIVERY.available = serviceModeStatus.CATERING_DELIVERY.capable;
  serviceModeStatus.CATERING_PICKUP.available = serviceModeStatus.CATERING_PICKUP.capable;
  serviceModeStatus.DELIVERY.available = serviceModeStatus.DELIVERY.capable && deliveryOpen;
  serviceModeStatus.EAT_IN.available = serviceModeStatus.EAT_IN.capable && diningRoomOpen;
  serviceModeStatus.CURBSIDE.available = serviceModeStatus.CURBSIDE.capable && curbsideOpen;
  serviceModeStatus.TAKEOUT.available = serviceModeStatus.TAKEOUT.capable && diningRoomOpen;
  serviceModeStatus.DRIVE_THRU.available = serviceModeStatus.DRIVE_THRU.capable && driveThruOpen;

  const allServiceModesUnavailable = Object.values(serviceModeStatus).every(
    ({ available, disabled }) => !available || disabled
  );

  const isRestaurantAvailable = useCallback(
    async (restaurantData: StoreProxy) => {
      const mobileOrderingEnabled = isMobileOrderingAvailable(restaurantData);
      const { number: storeId } = restaurantData;

      if (!storeId) {
        return false;
      }

      const rbiRestaurant = await getRestaurant(storeId);

      const isRestaurantPosAvailable = rbiRestaurant?.available;

      const open =
        !serviceMode ||
        [ServiceMode.DELIVERY, ServiceMode.CATERING_PICKUP, ServiceMode.CATERING_DELIVERY].includes(
          serviceMode
        ) ||
        (serviceModeStatus[serviceMode].available && !serviceModeStatus[serviceMode].disabled);

      return open && isRestaurantPosAvailable && mobileOrderingEnabled;
    },
    [serviceMode, serviceModeStatus] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Determines whether a restaurant can be selected */
  const restaurantCanBeSelected = useMemo(() => {
    // Store selection 2.0: any store can be selected
    // Return true if store has been selected
    if (storeSelection2_0Enabled && store._id) {
      return true;
    }

    const curbsideInAvailable =
      serviceModeStatus.CURBSIDE.available && !serviceModeStatus.CURBSIDE.disabled;
    const dineInAvailable =
      serviceModeStatus.EAT_IN.available && !serviceModeStatus.EAT_IN.disabled;
    const driveThruAvailable =
      serviceModeStatus.DRIVE_THRU.available && !serviceModeStatus.DRIVE_THRU.disabled;
    const takeoutAvailable =
      serviceModeStatus.TAKEOUT.available && !serviceModeStatus.TAKEOUT.disabled;

    // Store selection 1.0: store can only be selected if there is an available service mode
    return (
      isMobileOrderingAvailable(store) &&
      (curbsideInAvailable || dineInAvailable || driveThruAvailable || takeoutAvailable)
    );
  }, [serviceModeStatus, store, storeSelection2_0Enabled]);

  return {
    serviceModeStatus,
    allServiceModesUnavailable,
    isRestaurantAvailable,
    restaurantCanBeSelected
  };
};
